/**
 * ====================================================================
 * FIKO DATUM FEATURE - 3D COMPONENT
 * ====================================================================
 *
 * ASME Y14.5-2018 ve ISO 1101:2017 uyumlu profesyonel
 * Datum Feature 3D rendering komponenti.
 *
 * Özellikler:
 * - Datum Feature Symbol (kare çerçeve + harf)
 * - Datum Target (nokta, çizgi, alan)
 * - Datum Axis / Plane visualization
 * - Leader line ile geometriye bağlanma
 * - Zoom'a göre sabit boyut
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';
import { DatumType, DatumFeatureInfo, MaterialCondition, MATERIAL_CONDITION_SYMBOLS } from './GDTTypes';

// ====================================================================
// DATUM STYLE CONFIG
// ====================================================================

export interface DatumStyleConfig {
    /** Çerçeve rengi */
    frameColor?: string;

    /** Metin rengi */
    textColor?: string;

    /** Arka plan rengi */
    backgroundColor?: string;

    /** Çerçeve kalınlığı */
    frameWidth?: number;

    /** Font boyutu */
    fontSize?: number;

    /** Sabit boyut */
    fixedSize?: boolean;

    /** Leader rengi */
    leaderColor?: string;

    /** Leader kalınlığı */
    leaderWidth?: number;
}

const DEFAULT_DATUM_STYLE: Required<DatumStyleConfig> = {
    frameColor: '#00BCD4',
    textColor: '#FFFFFF',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    frameWidth: 2,
    fontSize: 18,
    fixedSize: true,
    leaderColor: '#00BCD4',
    leaderWidth: 1.5
};

// ====================================================================
// DATUM FEATURE CLASS
// ====================================================================

/**
 * Professional Datum Feature 3D Component
 */
export class DatumFeature extends THREE.Group {
    override type = 'DatumFeature';

    // ═══════════════════════════════════════════════════════════════
    // PROPERTIES
    // ═══════════════════════════════════════════════════════════════

    /** Sabit boyut */
    sabitBoyut: boolean = true;

    /** Datum bilgisi */
    private datumInfo: DatumFeatureInfo;

    /** Stil ayarları */
    private style: Required<DatumStyleConfig>;

    /** Sahne referansı */
    public sahne: FikoSahne;

    /** Canvas ve texture */
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    /** Sprite */
    private sprite: THREE.Sprite | null = null;

    /** Leader line */
    private leaderLine: THREE.Line | null = null;

    /** Datum triangle indicator */
    private datumIndicator: THREE.Mesh | null = null;

    /** Event listeners */
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    /** Boyutlar */
    private readonly SYMBOL_SIZE = 36;

    // ═══════════════════════════════════════════════════════════════
    // CONSTRUCTOR
    // ═══════════════════════════════════════════════════════════════

    constructor(
        sahne: FikoSahne,
        datumInfo: DatumFeatureInfo,
        style?: Partial<DatumStyleConfig>
    ) {
        super();

        this.sahne = sahne;
        this.datumInfo = datumInfo;
        this.style = { ...DEFAULT_DATUM_STYLE, ...style };
        this.sabitBoyut = this.style.fixedSize;

        // Canvas setup
        this.canvas = document.createElement('canvas');
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        // Name
        this.name = `Datum ${datumInfo.letter}`;
        this.renderOrder = 999;
        this.frustumCulled = false;

        // Initialize
        this.init();
        this.hazirla(sahne);
    }

    // ═══════════════════════════════════════════════════════════════
    // INITIALIZATION
    // ═══════════════════════════════════════════════════════════════

    private init(): void {
        this.renderToCanvas();
        this.createSprite();
        this.createDatumIndicator();
    }

    // ═══════════════════════════════════════════════════════════════
    // CANVAS RENDERING
    // ═══════════════════════════════════════════════════════════════

    private renderToCanvas(): void {
        const scale = 2;
        const size = this.SYMBOL_SIZE;

        this.canvas.width = size * scale;
        this.canvas.height = size * scale;

        const ctx = this.context;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, size, size);

        const padding = 3;
        const innerSize = size - padding * 2;

        // Background
        ctx.fillStyle = this.style.backgroundColor;
        ctx.fillRect(padding, padding, innerSize, innerSize);

        // Frame
        ctx.strokeStyle = this.style.frameColor;
        ctx.lineWidth = this.style.frameWidth;
        ctx.strokeRect(padding, padding, innerSize, innerSize);

        // Datum letter
        ctx.fillStyle = this.style.textColor;
        ctx.font = `bold ${this.style.fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.datumInfo.letter, size / 2, size / 2);

        // Update texture
        this.texture.needsUpdate = true;
    }

    // ═══════════════════════════════════════════════════════════════
    // SPRITE CREATION
    // ═══════════════════════════════════════════════════════════════

    private createSprite(): void {
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;
        this.texture.generateMipmaps = false;

        const material = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false,
            depthWrite: false
        });

        this.sprite = new THREE.Sprite(material);
        this.sprite.position.copy(this.datumInfo.position);
        this.sprite.scale.set(1, 1, 1);
        this.sprite.renderOrder = 999;
        this.add(this.sprite);
    }

    // ═══════════════════════════════════════════════════════════════
    // DATUM INDICATOR (Triangle)
    // ═══════════════════════════════════════════════════════════════

    private createDatumIndicator(): void {
        // Equilateral triangle pointing left
        const triangleShape = new THREE.Shape();
        const h = 0.3;
        const w = 0.25;

        triangleShape.moveTo(0, 0);
        triangleShape.lineTo(-w, h / 2);
        triangleShape.lineTo(-w, -h / 2);
        triangleShape.closePath();

        const geometry = new THREE.ShapeGeometry(triangleShape);
        const material = new THREE.MeshBasicMaterial({
            color: this.style.frameColor,
            side: THREE.DoubleSide,
            depthTest: false
        });

        this.datumIndicator = new THREE.Mesh(geometry, material);

        // Position to the left of sprite
        this.datumIndicator.position.copy(this.datumInfo.position);
        this.datumIndicator.position.x -= 0.6;
        this.datumIndicator.renderOrder = 998;

        this.add(this.datumIndicator);

        // Create connecting line
        this.createConnectingLine();
    }

    private createConnectingLine(): void {
        const positions = new Float32Array([
            this.datumInfo.position.x - 0.35, this.datumInfo.position.y, this.datumInfo.position.z,
            this.datumInfo.position.x - 0.6, this.datumInfo.position.y, this.datumInfo.position.z
        ]);

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        const material = new THREE.LineBasicMaterial({
            color: this.style.frameColor,
            linewidth: this.style.leaderWidth,
            depthTest: false
        });

        this.leaderLine = new THREE.Line(geometry, material);
        this.leaderLine.renderOrder = 998;
        this.add(this.leaderLine);
    }

    // ═══════════════════════════════════════════════════════════════
    // FIXED SIZE HANDLING
    // ═══════════════════════════════════════════════════════════════

    hazirla(sahne?: FikoSahne): void {
        if (!sahne?.anaGosterim?.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            console.warn('DatumFeature hazirla error:', e);
        }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;

        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;

        this.sprite.scale.set(
            originalScale.x * scaleAdjustment,
            originalScale.y * scaleAdjustment,
            1
        );
        this.sprite.updateMatrix();

        // Scale datum indicator
        if (this.datumIndicator) {
            this.datumIndicator.scale.set(scaleAdjustment, scaleAdjustment, 1);
        }
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.sprite) {
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
            this.kameraYonDegisti();
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // GETTERS
    // ═══════════════════════════════════════════════════════════════

    get letter(): string {
        return this.datumInfo.letter;
    }

    get datumType(): DatumType {
        return this.datumInfo.type;
    }

    get info(): DatumFeatureInfo {
        return { ...this.datumInfo };
    }

    // ═══════════════════════════════════════════════════════════════
    // UPDATE
    // ═══════════════════════════════════════════════════════════════

    updateInfo(newInfo: Partial<DatumFeatureInfo>): void {
        this.datumInfo = { ...this.datumInfo, ...newInfo };

        if (newInfo.letter) {
            this.name = `Datum ${newInfo.letter}`;
            this.renderToCanvas();
        }

        if (newInfo.position && this.sprite) {
            this.sprite.position.copy(newInfo.position);
            if (this.datumIndicator) {
                this.datumIndicator.position.copy(newInfo.position);
                this.datumIndicator.position.x -= 0.6;
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SERIALIZATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Serialize to JSON (avoid conflict with THREE.Object3D.toJSON)
     */
    serialize(): DatumFeatureInfo {
        return {
            ...this.datumInfo,
            position: this.datumInfo.position.clone()
        };
    }

    static fromJSON(sahne: FikoSahne, json: DatumFeatureInfo, style?: Partial<DatumStyleConfig>): DatumFeature {
        return new DatumFeature(sahne, {
            ...json,
            position: new THREE.Vector3(json.position.x, json.position.y, json.position.z)
        }, style);
    }

    // ═══════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        if (this.sprite) {
            (this.sprite.material as THREE.SpriteMaterial).map?.dispose();
            (this.sprite.material as THREE.SpriteMaterial).dispose();
            this.remove(this.sprite);
        }

        if (this.leaderLine) {
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
            this.remove(this.leaderLine);
        }

        if (this.datumIndicator) {
            this.datumIndicator.geometry.dispose();
            (this.datumIndicator.material as THREE.Material).dispose();
            this.remove(this.datumIndicator);
        }

        this.texture.dispose();
    }
}

// ====================================================================
// DATUM TARGET CLASS
// ====================================================================

/**
 * Datum Target - Nokta, çizgi veya alan hedefi
 */
export class DatumTarget extends THREE.Group {
    override type = 'DatumTarget';

    private targetId: string;
    private datumLetter: string;
    private targetType: 'POINT' | 'LINE' | 'AREA';

    private sahne: FikoSahne;
    private sprite: THREE.Sprite | null = null;
    private canvas: HTMLCanvasElement;
    private texture: THREE.CanvasTexture;

    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    constructor(
        sahne: FikoSahne,
        position: THREE.Vector3,
        datumLetter: string,
        targetNumber: number,
        targetType: 'POINT' | 'LINE' | 'AREA' = 'POINT'
    ) {
        super();

        this.sahne = sahne;
        this.datumLetter = datumLetter;
        this.targetId = `${datumLetter}${targetNumber}`;
        this.targetType = targetType;

        this.canvas = document.createElement('canvas');
        this.texture = new THREE.CanvasTexture(this.canvas);

        this.name = `Datum Target ${this.targetId}`;
        this.renderOrder = 999;

        this.init(position);
        this.hazirla(sahne);
    }

    private init(position: THREE.Vector3): void {
        this.renderToCanvas();
        this.createSprite(position);
    }

    private renderToCanvas(): void {
        const scale = 2;
        const size = 40;

        this.canvas.width = size * scale;
        this.canvas.height = size * scale;

        const ctx = this.canvas.getContext('2d', { alpha: true })!;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.clearRect(0, 0, size, size);

        const cx = size / 2;
        const cy = size / 2;
        const r = size / 2 - 3;

        // Outer circle
        ctx.strokeStyle = '#00BCD4';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();

        // Horizontal line (divider)
        ctx.beginPath();
        ctx.moveTo(cx - r, cy);
        ctx.lineTo(cx + r, cy);
        ctx.stroke();

        // Target ID in bottom half
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.targetId, cx, cy + r / 2);

        // Target type indicator in top half
        if (this.targetType === 'POINT') {
            // Cross
            ctx.beginPath();
            ctx.moveTo(cx - 4, cy - r / 2);
            ctx.lineTo(cx + 4, cy - r / 2);
            ctx.moveTo(cx, cy - r / 2 - 4);
            ctx.lineTo(cx, cy - r / 2 + 4);
            ctx.stroke();
        } else if (this.targetType === 'LINE') {
            // Line symbol
            ctx.beginPath();
            ctx.moveTo(cx - 6, cy - r / 2);
            ctx.lineTo(cx + 6, cy - r / 2);
            ctx.stroke();
        } else {
            // Area symbol (filled circle)
            ctx.beginPath();
            ctx.arc(cx, cy - r / 2, 4, 0, Math.PI * 2);
            ctx.fill();
        }

        this.texture.needsUpdate = true;
    }

    private createSprite(position: THREE.Vector3): void {
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;

        const material = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false
        });

        this.sprite = new THREE.Sprite(material);
        this.sprite.position.copy(position);
        this.sprite.scale.set(1, 1, 1);
        this.sprite.renderOrder = 999;
        this.add(this.sprite);
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne?.anaGosterim?.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;

        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;

        this.sprite.scale.set(
            originalScale.x * scaleAdjustment,
            originalScale.y * scaleAdjustment,
            1
        );
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kontrolDegistiEvent);
        }

        if (this.sprite) {
            (this.sprite.material as THREE.SpriteMaterial).map?.dispose();
            (this.sprite.material as THREE.SpriteMaterial).dispose();
            this.remove(this.sprite);
        }

        this.texture.dispose();
    }
}

// ====================================================================
// FACTORY FUNCTIONS
// ====================================================================

/**
 * Create datum feature
 */
export function createDatum(
    sahne: FikoSahne,
    position: THREE.Vector3,
    letter: string,
    options?: {
        type?: DatumType;
        geometryId?: string;
        surfaceType?: 'PLANE' | 'CYLINDER' | 'CONE' | 'SPHERE' | 'COMPLEX';
        normal?: THREE.Vector3;
        description?: string;
        style?: Partial<DatumStyleConfig>;
    }
): DatumFeature {
    return new DatumFeature(sahne, {
        id: `datum_${letter}_${Date.now()}`,
        letter: letter.toUpperCase(),
        type: options?.type || DatumType.SINGLE,
        position: position.clone(),
        geometryId: options?.geometryId,
        surfaceType: options?.surfaceType,
        normal: options?.normal,
        description: options?.description
    }, options?.style);
}

/**
 * Create datum target
 */
export function createDatumTarget(
    sahne: FikoSahne,
    position: THREE.Vector3,
    datumLetter: string,
    targetNumber: number,
    targetType: 'POINT' | 'LINE' | 'AREA' = 'POINT'
): DatumTarget {
    return new DatumTarget(sahne, position, datumLetter, targetNumber, targetType);
}
